/**
 * PATH: packages/ui/src/components/settings/email-label-prefs/LabelPreferencesSection.tsx
 * FILE_ID: UI-SETTINGS-LABELPREFS-SECTION-001
 * PURPOSE: The two-tier email label-preferences UI — Personal (private,
 *          editable) FIRST, then Organisation (shared, read-only).
 * WHY: Operator ask 2026-06-16 — "preferences of labelling are structured
 *      Personal -> Organisation ... the person who attached the mailbox might
 *      have a lot of personal stuff to categorise". Personal categories are
 *      private to the mailbox owner; org categories are the shared work tier.
 *
 * FLOW:
 *   mount → GET /email/label-preferences/ → render Personal group (CRUD) then
 *   Organisation group (read-only) → POST/PATCH/DELETE personal categories.
 */

import { useCallback, useEffect, useState } from 'react'
import { Loader2, Lock, Plus, Tag, Users } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { SettingsApiClient } from '../types'
import type { LabelCategory } from './types'
import {
  createOrgCategory,
  createPersonalCategory,
  deleteOrgCategory,
  deletePersonalCategory,
  fetchLabelPreferences,
  updateOrgCategory,
  updatePersonalCategory,
} from './labelPreferencesApi'
import { PersonalCategoryRow } from './PersonalCategoryRow'
import { nestingTargetsFor, orderLabelsNested } from './labelNesting'

export interface LabelPreferencesSectionProps {
  apiClient?: SettingsApiClient
  className?: string
}

export function LabelPreferencesSection({
  apiClient,
  className,
}: LabelPreferencesSectionProps) {
  const [personal, setPersonal] = useState<LabelCategory[]>([])
  const [organisation, setOrganisation] = useState<LabelCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | 'new' | 'new-org' | null>(null)
  const [newLabel, setNewLabel] = useState('')
  const [newOrgLabel, setNewOrgLabel] = useState('')

  const load = useCallback(async () => {
    if (!apiClient) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const prefs = await fetchLabelPreferences(apiClient)
      const p = prefs.groups.find((g) => g.id === 'personal')
      const o = prefs.groups.find((g) => g.id === 'organisation')
      setPersonal(p?.categories ?? [])
      setOrganisation(o?.categories ?? [])
    } catch {
      setError('Could not load label preferences.')
    } finally {
      setLoading(false)
    }
  }, [apiClient])

  useEffect(() => {
    load()
  }, [load])

  const handleAdd = useCallback(async () => {
    const label = newLabel.trim()
    if (!label || !apiClient) return
    setBusyId('new')
    try {
      const created = await createPersonalCategory(apiClient, { label })
      setPersonal((prev) => [...prev, created])
      setNewLabel('')
    } catch {
      setError('Could not add category.')
    } finally {
      setBusyId(null)
    }
  }, [apiClient, newLabel])

  const handleSave = useCallback(
    async (
      id: number,
      patch: {
        label?: string
        color?: string
        description?: string
        parent_id?: number | null
      },
    ) => {
      if (!apiClient) return
      setBusyId(id)
      try {
        const updated = await updatePersonalCategory(apiClient, id, patch)
        setPersonal((prev) => prev.map((c) => (c.id === id ? updated : c)))
      } catch {
        setError('Could not update category.')
      } finally {
        setBusyId(null)
      }
    },
    [apiClient],
  )

  const handleSaveOrg = useCallback(
    async (
      id: number,
      patch: {
        label?: string
        color?: string
        description?: string
        parent_id?: number | null
      },
    ) => {
      if (!apiClient) return
      setBusyId(id)
      try {
        const updated = await updateOrgCategory(apiClient, id, patch)
        setOrganisation((prev) => prev.map((c) => (c.id === id ? updated : c)))
      } catch {
        setError('Could not update team label.')
      } finally {
        setBusyId(null)
      }
    },
    [apiClient],
  )

  const handleDeleteOrg = useCallback(
    async (id: number) => {
      if (!apiClient) return
      setBusyId(id)
      try {
        await deleteOrgCategory(apiClient, id)
        setOrganisation((prev) => prev.filter((c) => c.id !== id))
      } catch {
        setError('Could not delete team label.')
      } finally {
        setBusyId(null)
      }
    },
    [apiClient],
  )

  const handleAddOrg = useCallback(async () => {
    const label = newOrgLabel.trim()
    if (!label || !apiClient) return
    setBusyId('new-org')
    try {
      const result = await createOrgCategory(apiClient, { label })
      if (result.kind === 'proposed') {
        setError(result.message)
        setNewOrgLabel('')
        return
      }
      setOrganisation((prev) => [...prev, result.category])
      setNewOrgLabel('')
    } catch {
      setError('Could not add team label.')
    } finally {
      setBusyId(null)
    }
  }, [apiClient, newOrgLabel])

  const handleDelete = useCallback(
    async (id: number) => {
      if (!apiClient) return
      setBusyId(id)
      try {
        await deletePersonalCategory(apiClient, id)
        setPersonal((prev) => prev.filter((c) => c.id !== id))
      } catch {
        setError('Could not delete category.')
      } finally {
        setBusyId(null)
      }
    },
    [apiClient],
  )

  if (!apiClient) return null

  return (
    <div className={cn('space-y-5 max-w-2xl pt-2', className)}>
      <div className="border-t border-slate-200 dark:border-slate-700 pt-5">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-slate-500" />
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">
            Labels &amp; categories
          </h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Your <span className="font-medium">Personal</span> labels are private
          to you. <span className="font-medium">Organisation</span> labels are
          shared work categories used across the team. Give a label an
          <span className="font-medium"> AI auto-file hint</span> and matching
          mail is filed there automatically.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading labels…
        </div>
      ) : (
        <>
          {error && (
            <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          {/* PERSONAL — private, editable, FIRST */}
          <section className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <header className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
              <Lock className="w-3.5 h-3.5 text-teal-600" />
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Personal
              </span>
              <span className="text-[11px] text-slate-400">Private to you</span>
            </header>
            <div className="p-1.5">
              {personal.length === 0 && (
                <p className="text-xs text-slate-400 px-2.5 py-2">
                  No personal labels yet. Add one below.
                </p>
              )}
              {orderLabelsNested(personal).map(({ category: cat, nested }) => (
                <PersonalCategoryRow
                  key={cat.id ?? cat.slug}
                  category={cat}
                  nested={nested}
                  parentOptions={nestingTargetsFor(personal, cat)}
                  busy={busyId === cat.id}
                  onSave={(patch) => cat.id != null && handleSave(cat.id, patch)}
                  onDelete={() => cat.id != null && handleDelete(cat.id)}
                />
              ))}
              <div className="flex items-center gap-2 px-2.5 py-2 mt-1 border-t border-dashed border-slate-200 dark:border-slate-700">
                <Plus className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  placeholder="Add a personal label (e.g. Investments)"
                  maxLength={80}
                  className="flex-1 px-2 py-1 rounded-md text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
                <button
                  onClick={handleAdd}
                  aria-label="Add personal label"
                  disabled={!newLabel.trim() || busyId === 'new'}
                  className="px-3 py-1.5 rounded-md bg-teal-600 text-white text-xs font-medium hover:bg-teal-700 disabled:opacity-50"
                >
                  {busyId === 'new' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    'Add'
                  )}
                </button>
              </div>
            </div>
          </section>

          {/* ORGANISATION — shared, SECOND. Built-ins are fixed; custom
              team labels (org.<key>) can be added by any member
              (operator 2026-07-03: "only able to create labels in
              Personal but not in system"). */}
          <section className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <header className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
              <Users className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Organisation
              </span>
              <span className="text-[11px] text-slate-400">
                Shared with your whole team
              </span>
            </header>
            <div className="flex flex-wrap gap-1.5 p-3">
              {organisation
                .filter((cat) => !(cat.editable && cat.id != null))
                .map((cat) => (
                  <span
                    key={cat.slug}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1 text-xs text-slate-600 dark:text-slate-300"
                  >
                    <span
                      className="w-2 h-2 rounded-full bg-slate-400"
                      style={cat.color ? { backgroundColor: cat.color } : undefined}
                    />
                    {cat.label}
                  </span>
                ))}
            </div>
            {/* Custom team labels — editable rows (rename / recolour /
                describe-for-AI / delete), same affordances as Personal. */}
            {organisation.some((cat) => cat.editable && cat.id != null) && (
              <div className="p-1.5 border-t border-dashed border-slate-200 dark:border-slate-700">
                {orderLabelsNested(
                  organisation.filter((cat) => cat.editable && cat.id != null),
                ).map(({ category: cat, nested }) => (
                  <PersonalCategoryRow
                    key={cat.slug}
                    category={cat}
                    nested={nested}
                    parentOptions={nestingTargetsFor(
                      organisation.filter((c) => c.editable && c.id != null),
                      cat,
                    )}
                    busy={busyId === cat.id}
                    onSave={(patch) => cat.id != null && handleSaveOrg(cat.id, patch)}
                    onDelete={() => cat.id != null && handleDeleteOrg(cat.id)}
                  />
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 px-2.5 py-2 border-t border-dashed border-slate-200 dark:border-slate-700">
              <Plus className="w-4 h-4 text-slate-400 shrink-0" />
              <input
                value={newOrgLabel}
                onChange={(e) => setNewOrgLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddOrg()}
                placeholder="Add a team label (e.g. Site Access)"
                maxLength={80}
                className="flex-1 px-2 py-1 rounded-md text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              />
              <button
                onClick={handleAddOrg}
                aria-label="Add team label"
                disabled={!newOrgLabel.trim() || busyId === 'new-org'}
                className="px-3 py-1.5 rounded-md bg-cyan-700 text-white text-xs font-medium hover:bg-cyan-800 disabled:opacity-50"
              >
                {busyId === 'new-org' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  'Add'
                )}
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  )
}

export default LabelPreferencesSection
